import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { Router } from '@angular/router';
import { ApiServiceService } from '../../services/common/api-service.service';
import Swal from 'sweetalert2';

export function ageValidator(control: AbstractControl): ValidationErrors | null {
  if (!control.value) return null;
  const birthDate = new Date(control.value);
  if (isNaN(birthDate.getTime())) return { invalidDate: true };
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const mDiff = today.getMonth() - birthDate.getMonth();
  if (mDiff < 0 || (mDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age < 18 ? { underAge: true } : null;
}

@Component({
  selector: 'app-signup-basic',
  templateUrl: './signup-basic.component.html',
  styleUrls: ['./signup-basic.component.scss'],
})
export class SignupBasicComponent implements OnInit, OnDestroy {
  @HostListener('document:click', ['$event'])
  clickOutside(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest('.custom-dropdown')) {
      this.showLanguageList = false;
    }
  }

  languages: any[] = [];
  signupForm!: FormGroup;
  errorMessage = '';
  selectedLanguageName: string = '';
  filteredLanguages: any[] = [];
  showLanguageList: boolean = false;
  maxDate: string = '';

  // ── OTP State ────────────────────────────────────────────────────────────
  showOtpModal = false;
  otpStep: 'channel' | 'verify' = 'channel';
  otpChannel: 'sms' | 'whatsapp' = 'sms';
  otpDigits: string[] = ['', '', '', '', '', ''];
  otpError = '';
  otpSending = false;
  otpVerifying = false;
  resendSeconds = 0;
  private resendInterval: any = null;
  readonly isLocalhost = typeof window !== 'undefined' && ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);

  constructor(
    private apiService: ApiServiceService,
    private formBuilder: FormBuilder,
    private router: Router,
  ) {
    this.signupForm = this.formBuilder.group({
      first_name: ['', Validators.required],
      last_name: ['', Validators.required],
      date_of_birth: ['', [Validators.required, ageValidator]],
      gender: ['Male', Validators.required],
      language: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      phone: ['', [Validators.required, Validators.pattern('^[0-9]{10}$')]],
      password: ['', [Validators.required, Validators.minLength(8)]],
    });
  }

  onPhoneInput(event: any): void {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/[^0-9]/g, '');
    if (value.length > 10) value = value.slice(0, 10);
    this.signupForm.get('phone')?.setValue(value);
  }

  ngOnInit(): void {
    const today = new Date();
    const maxYear = today.getFullYear() - 18;
    this.maxDate = new Date(maxYear, today.getMonth(), today.getDate()).toISOString().split('T')[0];
    this.apiService.getLanguages().subscribe({
      next: (response) => {
        this.languages = response.data;
        this.filteredLanguages = this.languages;
      },
      error: (err) => console.error('Error fetching languages:', err),
    });
  }

  ngOnDestroy(): void {
    this.clearResendTimer();
  }

  openLanguageDropdown() {
    this.filteredLanguages = this.languages;
    this.showLanguageList = true;
  }

  searchLanguage(event: any) {
    const searchText = event.target.value.toLowerCase();
    this.filteredLanguages = this.languages.filter((lang: any) =>
      lang.language_nme.toLowerCase().includes(searchText),
    );
    this.showLanguageList = true;
  }

  selectLanguage(lang: any) {
    this.selectedLanguageName = lang.language_nme;
    this.signupForm.patchValue({ language: lang._id });
    this.showLanguageList = false;
    this.filteredLanguages = this.languages;
  }

  // ── Form Submit → trigger OTP ─────────────────────────────────────────────
  onSubmit() {
    if (this.signupForm.get('date_of_birth')?.hasError('underAge')) {
      Swal.fire({
        title: 'Age Restriction',
        text: 'You must be at least 18 years old to register.',
        icon: 'error',
        confirmButtonColor: '#a73453',
      });
      return;
    }
    if (this.signupForm.valid) {
      this.openOtpModal();
    } else {
      this.signupForm.markAllAsTouched();
      Swal.fire({
        title: 'Form Invalid',
        text: 'Please check your inputs and try again.',
        icon: 'warning',
        confirmButtonColor: '#a73453',
      });
    }
  }

  // ── OTP Modal ─────────────────────────────────────────────────────────────
  openOtpModal() {
    this.otpStep = 'channel';
    this.otpDigits = ['', '', '', '', '', ''];
    this.otpError = '';
    this.showOtpModal = true;
  }

  closeOtpModal() {
    this.showOtpModal = false;
    this.clearResendTimer();
    this.otpDigits = ['', '', '', '', '', ''];
    this.otpError = '';
  }

  selectChannel(channel: 'sms' | 'whatsapp') {
    this.otpChannel = channel;
    this.otpError = '';
  }

  sendOtp() {
    const mobile = this.signupForm.value.phone;
    this.otpSending = true;
    this.otpError = '';
    this.clearResendTimer();
    //otp bypass temporary in localhost

    if (this.isLocalhost) {
      this.otpSending = false;
      this.otpDigits = ['1', '2', '3', '4', '5', '6'];
      this.otpStep = 'verify';
      this.startResendTimer();
      setTimeout(() => {
        const first = document.getElementById('otp-box-0') as HTMLInputElement;
        first?.focus();
        this.verifyOtp();
      }, 150);
      return;
    }
    //this upto added code for otp bypass
    this.apiService.sendOtp(mobile, this.otpChannel).subscribe({
      next: (res) => {
        this.otpSending = false;
        if (res.status === 'success') {
          this.otpStep = 'verify';
          this.startResendTimer();
          setTimeout(() => {
            const first = document.getElementById('otp-box-0') as HTMLInputElement;
            first?.focus();
          }, 100);
        } else {
          this.otpError = res.data || 'Failed to send OTP.';
        }
      },
      error: (err) => {
        this.otpSending = false;
        this.otpError = err.error?.data || 'Failed to send OTP. Please try again.';
      }
    });
  }

  resendOtp() {
    if (this.resendSeconds > 0) return;
    this.sendOtp();
  }

  switchChannelFromVerify() {
    this.otpChannel = this.otpChannel === 'sms' ? 'whatsapp' : 'sms';
    this.otpStep = 'channel';
    this.otpError = '';
  }

  trackByIndex(index: number): number {
    return index;
  }

  private lastInputTime = 0;
  private lastInputChar = '';
  private lastInputIndex = -1;

  // ── 6-digit input box handlers ─────────────────────────────────────────────
  onOtpInput(event: Event, index: number) {
    const input = event.target as HTMLInputElement;
    const val = input.value.replace(/\D/g, '');

    // Prevent duplicate focus bounce where browser fires second input on newly focused box
    if (val && val.length === 1 && index === this.lastInputIndex + 1 && val === this.lastInputChar && (Date.now() - this.lastInputTime) < 80) {
      input.value = '';
      return;
    }

    if (val.length > 1) {
      // Handle multiple digits (pasted or typed fast)
      for (let j = 0; j < val.length && (index + j) < 6; j++) {
        this.otpDigits[index + j] = val[j];
        const box = document.getElementById(`otp-box-${index + j}`) as HTMLInputElement;
        if (box) box.value = val[j];
      }
      this.otpDigits = [...this.otpDigits];
      const targetIndex = Math.min(index + val.length, 5);
      setTimeout(() => {
        const targetBox = document.getElementById(`otp-box-${targetIndex}`) as HTMLInputElement;
        targetBox?.focus();
      }, 15);
      return;
    }

    const singleDigit = val ? val[val.length - 1] : '';
    this.otpDigits[index] = singleDigit;
    this.otpDigits = [...this.otpDigits];
    input.value = singleDigit;

    if (singleDigit) {
      this.lastInputIndex = index;
      this.lastInputChar = singleDigit;
      this.lastInputTime = Date.now();
    }

    // Auto-advance with setTimeout to prevent key events from bleeding into the next box
    if (singleDigit && index < 5) {
      setTimeout(() => {
        const next = document.getElementById(`otp-box-${index + 1}`) as HTMLInputElement;
        if (next) {
          next.focus();
          next.select();
        }
      }, 15);
    }
  }

  onOtpKeydown(event: KeyboardEvent, index: number) {
    if (event.key === 'Backspace') {
      if (!this.otpDigits[index] && index > 0) {
        this.otpDigits[index - 1] = '';
        this.otpDigits = [...this.otpDigits];
        const prev = document.getElementById(`otp-box-${index - 1}`) as HTMLInputElement;
        if (prev) {
          prev.value = '';
          prev.focus();
        }
      } else {
        this.otpDigits[index] = '';
        this.otpDigits = [...this.otpDigits];
      }
    } else if (event.key === 'ArrowLeft' && index > 0) {
      const prev = document.getElementById(`otp-box-${index - 1}`) as HTMLInputElement;
      prev?.focus();
    } else if (event.key === 'ArrowRight' && index < 5) {
      const next = document.getElementById(`otp-box-${index + 1}`) as HTMLInputElement;
      next?.focus();
    }
  }

  onOtpPaste(event: ClipboardEvent) {
    event.preventDefault();
    const text = event.clipboardData?.getData('text').replace(/\D/g, '') || '';
    for (let i = 0; i < 6; i++) {
      this.otpDigits[i] = text[i] || '';
      const box = document.getElementById(`otp-box-${i}`) as HTMLInputElement;
      if (box) box.value = this.otpDigits[i];
    }
    this.otpDigits = [...this.otpDigits];
    const lastIdx = Math.min(text.length - 1, 5);
    setTimeout(() => {
      const box = document.getElementById(`otp-box-${lastIdx}`) as HTMLInputElement;
      box?.focus();
    }, 15);
  }

  get otpValue(): string {
    let digits = [...this.otpDigits];
    for (let i = 0; i < 6; i++) {
      if (!digits[i]) {
        const el = document.getElementById(`otp-box-${i}`) as HTMLInputElement;
        if (el && el.value && el.value.trim().length === 1) {
          digits[i] = el.value.trim();
          this.otpDigits[i] = digits[i];
        }
      }
    }
    return digits.join('');
  }

  get otpComplete(): boolean {
    return this.otpValue.length === 6;
  }

  verifyOtp() {
    if (!this.otpComplete || this.otpVerifying) return;
    const mobile = this.signupForm.value.phone;
    this.otpVerifying = true;
    this.otpError = '';

    if (this.isLocalhost) {
      setTimeout(() => {
        this.otpVerifying = false;
        this.closeOtpModal();
        this.doSignup();
      }, 200);
      return;
    }

    this.apiService.verifyOtp(mobile, this.otpValue).subscribe({
      next: (res) => {
        this.otpVerifying = false;
        if (res.status === 'success') {
          this.closeOtpModal();
          this.doSignup();
        } else {
          this.otpError = res.data || 'Incorrect OTP.';
        }
      },
      error: (err) => {
        this.otpVerifying = false;
        this.otpError = err.error?.data || 'Incorrect OTP. Please try again.';
        // Shake digits
        this.otpDigits = ['', '', '', '', '', ''];
        setTimeout(() => {
          const first = document.getElementById('otp-box-0') as HTMLInputElement;
          first?.focus();
        }, 0);
      }
    });
  }

  // ── After OTP verified → actual signup ────────────────────────────────────
  doSignup() {
    const userData = { ...this.signupForm.value, group: 'TEST' };
    this.apiService.signup(userData).subscribe({
      next: (response) => {
        if (response.status === 'success') {
          this.router.navigate(['/signup-religion']);
        } else {
          Swal.fire({
            title: 'Signup Failed',
            text: response.data || 'Signup failed.',
            icon: 'error',
            confirmButtonColor: '#a73453',
          });
        }
      },
      error: () => {
        Swal.fire({
          title: 'Error',
          text: 'An error occurred during signup. Please try again.',
          icon: 'error',
          confirmButtonColor: '#a73453',
        });
      },
    });
  }

  // ── Resend Timer ──────────────────────────────────────────────────────────
  startResendTimer() {
    this.resendSeconds = 30;
    this.resendInterval = setInterval(() => {
      this.resendSeconds--;
      if (this.resendSeconds <= 0) {
        this.clearResendTimer();
      }
    }, 1000);
  }

  clearResendTimer() {
    if (this.resendInterval) {
      clearInterval(this.resendInterval);
      this.resendInterval = null;
    }
    this.resendSeconds = 0;
  }
}
